import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth, ROLE_LABELS, postAuthDestination } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { startGoogleOAuth } from "@/lib/googleAuth";

export default function OnboardingAuth() {
  const navigate = useNavigate();
  const { register, login, user } = useAuth();
  const [mode, setMode] = useState("register");  // register | login
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const role = sessionStorage.getItem("kb_onboarding_role") || "client";

  useEffect(() => { if (user) navigate(postAuthDestination(user)); }, [user, navigate]);
  useEffect(() => {
    if (!sessionStorage.getItem("kb_onboarding_role")) {
      navigate("/onboarding/role", { replace: true });
    }
  }, [navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleOAuth({ role });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && form.password.length < 6) {
      toast.error("Password must be at least 6 characters"); return;
    }
    setSubmitting(true);
    try {
      let u;
      if (mode === "register") {
        u = await register({ name: form.name, email: form.email, password: form.password, role });
        toast.success("Account created — let's complete your profile.");
        navigate("/onboarding/profile");
      } else {
        u = await login(form.email, form.password);
        toast.success("Welcome back!");
        navigate(postAuthDestination(u));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (mode === "register" ? "Registration failed" : "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-kb-bg flex flex-col" data-testid="onboarding-auth-page">
      <Navbar />
      <div className="flex-1 max-w-md mx-auto w-full px-6 py-14">
        <Link to="/onboarding/role" className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2" data-testid="auth-back">
          <ArrowLeft className="w-3.5 h-3.5" /> CHANGE ROLE
        </Link>
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-6">STEP 2 OF 3 · {ROLE_LABELS[role]}</div>
        <h1 className="font-serif text-3xl text-kb-ink mt-1 leading-tight">
          {mode === "register" ? <>Create your <em className="italic text-kb-primary">account</em>.</> : <>Welcome <em className="italic text-kb-primary">back</em>.</>}
        </h1>

        <button onClick={googleLogin}
          className="mt-8 w-full border border-slate-300 bg-white text-kb-ink rounded-sm px-4 py-3 flex items-center justify-center gap-3 font-medium hover:border-kb-primary transition-colors"
          data-testid="onboarding-google-btn">
          <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.8V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.6 5.9 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8c1.8-4.4 6-7.5 11.1-7.5 3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.6 5.9 29 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 13-5l-6-5.1c-1.9 1.4-4.4 2.3-7 2.3-5.3 0-9.7-3.4-11.3-8.1l-6.6 5.1C9.4 39.6 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 3.8-3.9 5l6 5.1c-.4.4 6.6-4.8 6.6-14.1 0-1.2-.1-2.3-.4-3.5z"/></svg>
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-light">OR</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Full name</label>
              <input required name="name" className="kb-input mt-1.5" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="onboarding-name-input" />
            </div>
          )}
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Email</label>
            <input type="email" name="email" required className="kb-input mt-1.5" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="onboarding-email-input" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Password</label>
            <input type="password" name="password" required minLength={6} className="kb-input mt-1.5" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="onboarding-password-input" />
          </div>
          <button type="submit" disabled={submitting} className="kb-btn-primary w-full justify-center" data-testid="onboarding-auth-submit">
            {submitting ? "Working…" : (<>{mode === "register" ? "Create account" : "Sign in"} <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>

        <div className="mt-6 text-sm text-kb-ink-muted text-center">
          {mode === "register" ? (
            <>Already have an account? <button onClick={() => setMode("login")} className="text-kb-primary font-medium" data-testid="auth-switch-login">Sign in</button></>
          ) : (
            <>New here? <button onClick={() => setMode("register")} className="text-kb-primary font-medium" data-testid="auth-switch-register">Create account</button></>
          )}
        </div>
      </div>
    </div>
  );
}
